export type StaffPermission =
  | 'manager.override'
  | 'staff.admin'
  | 'store.settings.draft.write'
  | 'store.settings.validate'
  | 'store.settings.publish'
  | 'store.settings.activate'
  | 'store.settings.rollback'
  | 'catalog.write'
  | 'catalog.publish'
  | 'availability.change'
  | 'availability.schedule'
  | 'inventory.configure'
  | 'inventory.receive'
  | 'inventory.adjust'
  | 'inventory.stocktake'
  | 'bom.write'
  | 'bom.publish'
  | 'bom.recover'
  | 'procurement.manage'
  | 'customer.manage'
  | 'loyalty.adjust'
  | 'table.open'
  | 'table.transfer'
  | 'table.merge'
  | 'table.split'
  | 'table.clear'
  | 'order.sensitive-correction'
  | 'payment.refund'
  | 'payment.correction'
  | 'business-day.open'
  | 'service.manage'
  | 'shift.open'
  | 'cash.movement'
  | 'shift.close'
  | 'report.view.history'
  | 'report.export';

export type StaffStatus = 'active' | 'inactive';

export interface StaffAuthorityOptions {
  readonly rootActorIds: readonly string[];
}

export interface StaffMutationContext {
  readonly actorId: string;
  readonly at: string;
  readonly idempotencyKey: string;
}

export interface StaffRoleInput {
  readonly roleId: string;
  readonly permissions: readonly StaffPermission[];
}

export interface StaffRoleSnapshot extends StaffRoleInput {
  readonly version: number;
  readonly createdAt: string;
  readonly createdBy: string;
  readonly updatedAt: string;
  readonly updatedBy: string;
}

export interface UpdateStaffRoleCommand extends StaffRoleInput {
  readonly expectedVersion: number;
}

export interface StaffInput {
  readonly staffId: string;
  readonly displayName: string;
  readonly roleIds: readonly string[];
}

export interface StaffSnapshot extends StaffInput {
  readonly status: StaffStatus;
  readonly version: number;
  readonly createdAt: string;
  readonly createdBy: string;
  readonly updatedAt: string;
  readonly updatedBy: string;
}

export interface UpdateStaffCommand extends StaffInput {
  readonly expectedVersion: number;
}

export interface SetStaffStatusCommand {
  readonly staffId: string;
  readonly expectedVersion: number;
  readonly status: StaffStatus;
}

export interface StaffAuthorizationCommand {
  readonly actorId: string;
  readonly permission: StaffPermission;
  readonly overrideActorId?: string;
  readonly at: string;
}

export interface StaffAuthorizationResult {
  readonly allowed: true;
  readonly actorId: string;
  readonly permission: StaffPermission;
  readonly authorizedBy: string;
  readonly viaOverride: boolean;
}

export interface StaffAuditEvent {
  readonly type: 'role.created' | 'role.updated' | 'staff.created' | 'staff.updated' | 'staff.status.changed';
  readonly at: string;
  readonly actorId: string;
  readonly roleId?: string;
  readonly staffId?: string;
  readonly version: number;
}

interface IdempotentResult {
  readonly signature: string;
  readonly result: unknown;
}

const STAFF_PERMISSIONS: readonly StaffPermission[] = [
  'manager.override',
  'staff.admin',
  'store.settings.draft.write',
  'store.settings.validate',
  'store.settings.publish',
  'store.settings.activate',
  'store.settings.rollback',
  'catalog.write',
  'catalog.publish',
  'availability.change',
  'availability.schedule',
  'inventory.configure',
  'inventory.receive',
  'inventory.adjust',
  'inventory.stocktake',
  'bom.write',
  'bom.publish',
  'bom.recover',
  'procurement.manage',
  'customer.manage',
  'loyalty.adjust',
  'table.open',
  'table.transfer',
  'table.merge',
  'table.split',
  'table.clear',
  'order.sensitive-correction',
  'payment.refund',
  'payment.correction',
  'business-day.open',
  'service.manage',
  'shift.open',
  'cash.movement',
  'shift.close',
  'report.view.history',
  'report.export',
];

const clone = <T>(value: T): T => structuredClone(value);

const stableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, entry]) => [key, stableValue(entry)]),
    );
  }
  return value;
};

const signatureOf = (value: unknown): string => JSON.stringify(stableValue(value));

const assertText = (value: string, code: string): void => {
  if (!value?.trim()) throw new Error(code);
};

const assertTime = (value: string): void => {
  if (!Number.isFinite(Date.parse(value))) throw new Error('STAFF_TIMESTAMP_INVALID');
};

const assertVersion = (value: number, code: string): void => {
  if (!Number.isSafeInteger(value) || value < 1) throw new Error(code);
};

const normalizeTextSet = (values: readonly string[], duplicateCode: string): string[] => {
  const normalized = values.map((value) => value.trim());
  if (normalized.some((value) => !value)) throw new Error('STAFF_REFERENCE_REQUIRED');
  const unique = new Set(normalized);
  if (unique.size !== normalized.length) throw new Error(duplicateCode);
  return [...unique].sort();
};

const normalizePermissions = (permissions: readonly StaffPermission[]): StaffPermission[] => {
  const normalized = normalizeTextSet(permissions, 'STAFF_PERMISSION_DUPLICATE');
  for (const permission of normalized) {
    if (!STAFF_PERMISSIONS.includes(permission as StaffPermission)) {
      throw new Error(`STAFF_PERMISSION_INVALID:${permission}`);
    }
  }
  return normalized as StaffPermission[];
};

export function createStaffAuthority(options: StaffAuthorityOptions) {
  const rootActorIds = new Set(normalizeTextSet(options.rootActorIds, 'STAFF_ROOT_ACTOR_DUPLICATE'));
  if (rootActorIds.size === 0) throw new Error('STAFF_ROOT_ACTOR_REQUIRED');

  const roles = new Map<string, StaffRoleSnapshot>();
  const staff = new Map<string, StaffSnapshot>();
  const idempotency = new Map<string, IdempotentResult>();
  const auditEvents: StaffAuditEvent[] = [];

  const assertMutationContext = (context: StaffMutationContext): void => {
    assertText(context.actorId, 'STAFF_MUTATION_ACTOR_REQUIRED');
    assertText(context.idempotencyKey, 'STAFF_IDEMPOTENCY_REQUIRED');
    assertTime(context.at);
  };

  const getRole = (roleId: string): StaffRoleSnapshot => {
    const role = roles.get(roleId);
    if (!role) throw new Error('STAFF_ROLE_NOT_FOUND');
    return role;
  };

  const getStaff = (staffId: string): StaffSnapshot => {
    const current = staff.get(staffId);
    if (!current) throw new Error('STAFF_NOT_FOUND');
    return current;
  };

  const normalizeRoleIds = (roleIds: readonly string[]): string[] => {
    const normalized = normalizeTextSet(roleIds, 'STAFF_ROLE_ID_DUPLICATE');
    for (const roleId of normalized) getRole(roleId);
    return normalized;
  };

  const permissionSetFor = (snapshot: StaffSnapshot): Set<StaffPermission> => {
    const permissions = new Set<StaffPermission>();
    for (const roleId of snapshot.roleIds) {
      for (const permission of getRole(roleId).permissions) permissions.add(permission);
    }
    return permissions;
  };

  const assertActive = (snapshot: StaffSnapshot, override = false): void => {
    if (snapshot.status !== 'active') {
      throw new Error(override ? 'STAFF_OVERRIDE_INACTIVE' : 'STAFF_INACTIVE');
    }
  };

  const assertStaffAdmin = (context: StaffMutationContext): void => {
    if (rootActorIds.has(context.actorId)) return;
    const actor = staff.get(context.actorId);
    if (!actor) throw new Error('STAFF_PERMISSION_DENIED:staff.admin');
    assertActive(actor);
    if (!permissionSetFor(actor).has('staff.admin')) throw new Error('STAFF_PERMISSION_DENIED:staff.admin');
  };

  const replayOrRun = <T>(
    context: StaffMutationContext,
    signature: string,
    action: () => T,
  ): T => {
    assertMutationContext(context);
    const existing = idempotency.get(context.idempotencyKey);
    if (existing) {
      if (existing.signature !== signature) throw new Error('STAFF_IDEMPOTENCY_CONFLICT');
      return clone(existing.result as T);
    }
    const result = action();
    idempotency.set(context.idempotencyKey, { signature, result: clone(result) });
    return clone(result);
  };

  return {
    createRole(input: StaffRoleInput, context: StaffMutationContext): StaffRoleSnapshot {
      assertText(input.roleId, 'STAFF_ROLE_ID_REQUIRED');
      const accepted = {
        roleId: input.roleId.trim(),
        permissions: normalizePermissions(input.permissions),
      };
      const signature = signatureOf({ kind: 'role.create', input: accepted, actorId: context.actorId });
      return replayOrRun(context, signature, () => {
        assertStaffAdmin(context);
        if (roles.has(accepted.roleId)) throw new Error('STAFF_ROLE_ID_CONFLICT');
        const snapshot: StaffRoleSnapshot = {
          ...accepted,
          version: 1,
          createdAt: context.at,
          createdBy: context.actorId,
          updatedAt: context.at,
          updatedBy: context.actorId,
        };
        roles.set(snapshot.roleId, snapshot);
        auditEvents.push({
          type: 'role.created', at: context.at, actorId: context.actorId,
          roleId: snapshot.roleId, version: snapshot.version,
        });
        return snapshot;
      });
    },

    updateRole(command: UpdateStaffRoleCommand, context: StaffMutationContext): StaffRoleSnapshot {
      assertText(command.roleId, 'STAFF_ROLE_ID_REQUIRED');
      assertVersion(command.expectedVersion, 'STAFF_ROLE_EXPECTED_VERSION_INVALID');
      const accepted = {
        roleId: command.roleId.trim(),
        expectedVersion: command.expectedVersion,
        permissions: normalizePermissions(command.permissions),
      };
      const signature = signatureOf({ kind: 'role.update', command: accepted, actorId: context.actorId });
      return replayOrRun(context, signature, () => {
        assertStaffAdmin(context);
        const current = getRole(accepted.roleId);
        if (current.version !== accepted.expectedVersion) throw new Error('STAFF_ROLE_VERSION_CONFLICT');
        const snapshot: StaffRoleSnapshot = {
          ...current,
          permissions: accepted.permissions,
          version: current.version + 1,
          updatedAt: context.at,
          updatedBy: context.actorId,
        };
        roles.set(snapshot.roleId, snapshot);
        auditEvents.push({
          type: 'role.updated', at: context.at, actorId: context.actorId,
          roleId: snapshot.roleId, version: snapshot.version,
        });
        return snapshot;
      });
    },

    createStaff(input: StaffInput, context: StaffMutationContext): StaffSnapshot {
      assertText(input.staffId, 'STAFF_ID_REQUIRED');
      assertText(input.displayName, 'STAFF_DISPLAY_NAME_REQUIRED');
      const accepted = {
        staffId: input.staffId.trim(),
        displayName: input.displayName.trim(),
        roleIds: normalizeRoleIds(input.roleIds),
      };
      const signature = signatureOf({ kind: 'staff.create', input: accepted, actorId: context.actorId });
      return replayOrRun(context, signature, () => {
        assertStaffAdmin(context);
        if (staff.has(accepted.staffId)) throw new Error('STAFF_ID_CONFLICT');
        const snapshot: StaffSnapshot = {
          ...accepted,
          status: 'active',
          version: 1,
          createdAt: context.at,
          createdBy: context.actorId,
          updatedAt: context.at,
          updatedBy: context.actorId,
        };
        staff.set(snapshot.staffId, snapshot);
        auditEvents.push({
          type: 'staff.created', at: context.at, actorId: context.actorId,
          staffId: snapshot.staffId, version: snapshot.version,
        });
        return snapshot;
      });
    },

    updateStaff(command: UpdateStaffCommand, context: StaffMutationContext): StaffSnapshot {
      assertText(command.staffId, 'STAFF_ID_REQUIRED');
      assertText(command.displayName, 'STAFF_DISPLAY_NAME_REQUIRED');
      assertVersion(command.expectedVersion, 'STAFF_EXPECTED_VERSION_INVALID');
      const accepted = {
        staffId: command.staffId.trim(),
        expectedVersion: command.expectedVersion,
        displayName: command.displayName.trim(),
        roleIds: normalizeRoleIds(command.roleIds),
      };
      const signature = signatureOf({ kind: 'staff.update', command: accepted, actorId: context.actorId });
      return replayOrRun(context, signature, () => {
        assertStaffAdmin(context);
        const current = getStaff(accepted.staffId);
        if (current.version !== accepted.expectedVersion) throw new Error('STAFF_VERSION_CONFLICT');
        const snapshot: StaffSnapshot = {
          ...current,
          displayName: accepted.displayName,
          roleIds: accepted.roleIds,
          version: current.version + 1,
          updatedAt: context.at,
          updatedBy: context.actorId,
        };
        staff.set(snapshot.staffId, snapshot);
        auditEvents.push({
          type: 'staff.updated', at: context.at, actorId: context.actorId,
          staffId: snapshot.staffId, version: snapshot.version,
        });
        return snapshot;
      });
    },

    setStaffStatus(command: SetStaffStatusCommand, context: StaffMutationContext): StaffSnapshot {
      assertText(command.staffId, 'STAFF_ID_REQUIRED');
      assertVersion(command.expectedVersion, 'STAFF_EXPECTED_VERSION_INVALID');
      if (command.status !== 'active' && command.status !== 'inactive') throw new Error('STAFF_STATUS_INVALID');
      const signature = signatureOf({ kind: 'staff.status', command, actorId: context.actorId });
      return replayOrRun(context, signature, () => {
        assertStaffAdmin(context);
        const current = getStaff(command.staffId);
        if (current.version !== command.expectedVersion) throw new Error('STAFF_VERSION_CONFLICT');
        const snapshot: StaffSnapshot = {
          ...current,
          status: command.status,
          version: current.version + 1,
          updatedAt: context.at,
          updatedBy: context.actorId,
        };
        staff.set(snapshot.staffId, snapshot);
        auditEvents.push({
          type: 'staff.status.changed', at: context.at, actorId: context.actorId,
          staffId: snapshot.staffId, version: snapshot.version,
        });
        return snapshot;
      });
    },

    authorize(command: StaffAuthorizationCommand): StaffAuthorizationResult {
      assertText(command.actorId, 'STAFF_AUTHORIZATION_ACTOR_REQUIRED');
      assertTime(command.at);
      const actor = getStaff(command.actorId);
      assertActive(actor);
      const actorPermissions = permissionSetFor(actor);

      if (actorPermissions.has(command.permission)) {
        return {
          allowed: true,
          actorId: actor.staffId,
          permission: command.permission,
          authorizedBy: actor.staffId,
          viaOverride: false,
        };
      }

      if (!command.overrideActorId) throw new Error(`STAFF_PERMISSION_DENIED:${command.permission}`);
      const overrideActor = getStaff(command.overrideActorId);
      assertActive(overrideActor, true);
      const overridePermissions = permissionSetFor(overrideActor);
      if (!overridePermissions.has('manager.override')) throw new Error('STAFF_OVERRIDE_NOT_ALLOWED');
      if (!overridePermissions.has(command.permission)) {
        throw new Error(`STAFF_OVERRIDE_PERMISSION_DENIED:${command.permission}`);
      }

      return {
        allowed: true,
        actorId: actor.staffId,
        permission: command.permission,
        authorizedBy: overrideActor.staffId,
        viaOverride: true,
      };
    },

    role(roleId: string): StaffRoleSnapshot | null {
      const current = roles.get(roleId);
      return current ? clone(current) : null;
    },

    staff(staffId: string): StaffSnapshot | null {
      const current = staff.get(staffId);
      return current ? clone(current) : null;
    },

    audit(): readonly StaffAuditEvent[] {
      return auditEvents.map((event) => clone(event));
    },
  };
}
